import { UserIcon } from "@heroicons/react/24/outline";
import { CalendarDate, CommunityId, User, UserId } from "@sp-crm/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { TourData } from "components/community-search/types";
import {
    ContentFull,
    ContentHeader,
    ContentSection2Up,
    ContentSectionColumn,
} from "components/layout";
import { trpc } from "components/rpc/trpc";
import { TourEditor } from "components/tours/tour-editor";
import { DeleteButton, EditButton } from "components/ui/action-button";
import { ClampedText } from "components/ui/clamped-text";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import React, { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { breakNewlines } from "util/text";
import { formatTourDate, formatTourTime, splitTours } from "util/tour";

interface CommunityToursTabProps {
    communityId: CommunityId;
    users: Record<UserId, User>;
}

const mapToTourData = (tour: {
    id: string;
    communityId: string;
    clientId?: string | null;
    clientName?: string | null;
    userId: string;
    tourDate: string;
    tourTime?: string | null;
    privateNotes?: string | null;
    familyNotes?: string | null;
    dateAdded: string;
    updatedAt: string;
}): TourData => {
    const tourDateSerialized = CalendarDate.parse(tour.tourDate)
        .map(cd => cd.serialize())
        .getOrElse(CalendarDate.today.serialize());
    return {
        id: tour.id,
        communityId: tour.communityId,
        clientId: tour.clientId,
        clientName: tour.clientName,
        userId: tour.userId,
        tourDate: tourDateSerialized,
        tourTime: tour.tourTime,
        privateNotes: tour.privateNotes,
        familyNotes: tour.familyNotes,
        dateAdded: tour.dateAdded,
        updatedAt: tour.updatedAt,
    } as TourData;
};

const TourItem: React.FC<{
    tour: TourData;
    users: Record<UserId, User>;
    onEdit: (tour: TourData) => void;
    onDelete: (tour: TourData) => void;
}> = ({ tour, users, onEdit, onDelete }) => {
    const userName = users[tour.userId]?.name || "";

    const handleEdit = useCallback(() => {
        onEdit(tour);
    }, [onEdit, tour]);

    const handleDelete = useCallback(() => {
        onDelete(tour);
    }, [onDelete, tour]);

    return (
        <li className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-start">
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base">
                            {formatTourDate(tour.tourDate)}
                            {formatTourTime(tour.tourTime)}
                        </span>
                        {tour.clientId && tour.clientName ? (
                            <Link
                                to={`/clients/show/${tour.clientId}`}
                                className="inline-flex items-center gap-1 text-sm text-brand-600 hover:text-brand-800 hover:underline">
                                <UserIcon className="h-3.5 w-3.5" aria-hidden="true" />
                                {tour.clientName}
                            </Link>
                        ) : null}
                    </div>
                    {userName ? (
                        <div className="text-xs text-gray-500 mt-0.5">{userName}</div>
                    ) : null}
                    {tour.familyNotes ? (
                        <div className="mt-2 space-y-0.5">
                            <div className="text-sm font-medium text-gray-500">
                                Tour Notes
                            </div>
                            <div className="text-sm text-gray-900">
                                <ClampedText lines={3}>
                                    {breakNewlines(tour.familyNotes)}
                                </ClampedText>
                            </div>
                        </div>
                    ) : null}
                    {tour.privateNotes ? (
                        <div className="mt-2 space-y-0.5">
                            <div className="text-sm font-medium text-gray-500">
                                Internal Notes
                            </div>
                            <div className="text-sm text-gray-900">
                                <ClampedText lines={3}>
                                    {breakNewlines(tour.privateNotes)}
                                </ClampedText>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center">
                    <EditButton backgroundColor="bg-white" onClick={handleEdit} />
                    <DeleteButton
                        backgroundColor="bg-white"
                        onClick={handleDelete}
                        confirm={{
                            title: "Delete Tour",
                            message: "Are you sure you want to delete this tour?",
                        }}
                    />
                </div>
            </div>
        </li>
    );
};

export const CommunityToursTab: React.FC<CommunityToursTabProps> = props => {
    const { communityId, users } = props;
    const [editingTour, setEditingTour] = useState<TourData | null | "new">(null);

    const trpcClient = trpc.useTRPC();
    const toursQuery = useQuery({
        ...trpcClient.tours.listByCommunity.queryOptions({ communityId }),
    });

    const tours = useMemo(
        () => (toursQuery.data || []).map(mapToTourData),
        [toursQuery.data],
    );

    const { upcoming, past } = useMemo(() => splitTours(tours), [tours]);

    const handleAddTour = useCallback(() => {
        setEditingTour("new");
    }, []);

    const handleEditTour = useCallback((tour: TourData) => {
        setEditingTour(tour);
    }, []);

    const handleClose = useCallback(() => {
        setEditingTour(null);
    }, []);

    const handleSaved = useCallback(() => {
        toursQuery.refetch();
    }, [toursQuery]);

    const deleteMutation = useMutation({
        ...trpcClient.tours.delete.mutationOptions(),
        onSuccess: () => {
            toursQuery.refetch();
        },
    });

    const handleDeleteTour = useCallback(
        (tour: TourData) => {
            deleteMutation.mutate({ id: tour.id });
        },
        [deleteMutation],
    );

    if (toursQuery.isLoading) {
        return (
            <div className="flex justify-center py-8">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end">
                <PrimaryButton onClick={handleAddTour}>Add Tour</PrimaryButton>
            </div>

            <ContentSection2Up>
                <ContentSectionColumn>
                    <ContentFull>
                        <ContentHeader>Upcoming Tours</ContentHeader>
                        {upcoming.length > 0 ? (
                            <ul className="space-y-2 mt-3">
                                {upcoming.map(tour => (
                                    <TourItem
                                        key={tour.id}
                                        tour={tour}
                                        users={users}
                                        onEdit={handleEditTour}
                                        onDelete={handleDeleteTour}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">
                                No upcoming tours
                            </p>
                        )}
                    </ContentFull>
                </ContentSectionColumn>
                <ContentSectionColumn>
                    <ContentFull>
                        <ContentHeader>Past Tours</ContentHeader>
                        {past.length > 0 ? (
                            <ul className="space-y-2 mt-3">
                                {past.map(tour => (
                                    <TourItem
                                        key={tour.id}
                                        tour={tour}
                                        users={users}
                                        onEdit={handleEditTour}
                                        onDelete={handleDeleteTour}
                                    />
                                ))}
                            </ul>
                        ) : (
                            <p className="text-sm text-gray-500 mt-2">No past tours</p>
                        )}
                    </ContentFull>
                </ContentSectionColumn>
            </ContentSection2Up>

            {editingTour !== null ? (
                <TourEditor
                    tour={editingTour === "new" ? null : editingTour}
                    communityId={communityId}
                    onSaved={handleSaved}
                    onClose={handleClose}
                />
            ) : null}
        </div>
    );
};
